/**
 * @file UserVoiceEnrollmentBadge.tsx
 * @brief Voice enrollment indicator — typically overlaid on the Users avatar.
 *
 * @details
 * Meeting Intel Phase 1 stores enrollment as `none` | `enrolled` on the
 * users row. Super admins scan the mic icon; hover shows the full label.
 *
 * @references
 * - BACKEND/src/features/auth/voiceEnrollment.service.js
 * - docs/product/voice-enrollment.md
 *
 * @author DES
 * @date 11-07-2026
 * @updated 29-07-2026 — avatar badge (Voice column removed)
 */

import { Mic, MicOff } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { cn } from "@/shared/theme/cn";

export type VoiceEnrollmentStatus = "enrolled" | "none" | string;

export function UserVoiceEnrollmentBadge({
  status,
  size = "md",
}: {
  status: VoiceEnrollmentStatus | null | undefined;
  /** `sm` — corner chip on avatar; `md` — standalone control. */
  size?: "sm" | "md";
}) {
  const enrolled = status === "enrolled";
  const label = enrolled ? "Voice enrolled" : "Voice not enrolled";
  const Icon = enrolled ? Mic : MicOff;
  const compact = size === "sm";

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex items-center justify-center rounded-full border border-background shadow-sm",
              compact ? "h-5 w-5" : "h-8 w-8",
              enrolled
                ? "bg-success-soft text-success"
                : "bg-muted text-muted-foreground",
            )}
            aria-label={label}
          >
            <Icon
              className={cn(compact ? "h-3 w-3" : "h-4 w-4")}
              aria-hidden
            />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
